import { LOAD_CAPACITY, fmt } from "./calc";
import { LOGO_URL } from "../components/Logo";

export function buildLoadSummary(load, panelsById) {
  const panels = (load.panelIds || []).map((id) => panelsById[id]).filter(Boolean);
  const totalVol = panels.reduce((s, p) => s + (p.volume || 0), 0);
  const totalMass = panels.reduce((s, p) => s + (p.mass || 0), 0);
  const jobs = [...new Set(panels.map((p) => p.jobNumber || "—"))];
  const strengths = [...new Set(panels.map((p) => p.strength28).filter((x) => x != null && x > 0))];
  const mpa = strengths.length === 0 ? "—" : strengths.length === 1 ? `${strengths[0]} MPa` : `Mixed (${strengths.join("/")}) MPa`;
  return { panels, totalVol, totalMass, jobs, strengths, mpa };
}

function csvEscape(v) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

function slug(name) {
  return (name || "").trim().replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
}

export function downloadLoadCsv(load, panelsById, projectName = "") {
  const { panels, totalVol, totalMass } = buildLoadSummary(load, panelsById);
  const header = ["Job #", "Mark", "Width(m)", "Length(m)", "Thickness(m)", "Area(m2)", "Volume(m3)", "Mass(t)", "f'c(MPa)", "Shape"];
  const rows = [];
  if (projectName) rows.push([`Yard: ${projectName}`]);
  rows.push(header);
  panels.forEach((p) =>
    rows.push([p.jobNumber, p.name, p.width, p.length, p.thickness, p.area, p.volume, (p.mass / 1000).toFixed(2), p.strength28, p.shape])
  );
  rows.push([]);
  rows.push(["", "TOTAL", "", "", "", "", totalVol.toFixed(2), (totalMass / 1000).toFixed(2), "", ""]);
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const prefix = projectName ? slug(projectName) + "_" : "";
  a.download = `${prefix}${(load.label || "load").replace(/\s+/g, "_")}_booking_sheet.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadAllLoadsCsv(loads, panelsById, projectName = "") {
  const header = ["Load", "Job #", "Mark", "Width(m)", "Length(m)", "Thickness(m)", "Area(m2)", "Volume(m3)", "Mass(t)", "f'c(MPa)", "Shape"];
  const rows = [];
  if (projectName) rows.push([`Yard: ${projectName}`]);
  rows.push(header);
  loads.forEach((load) => {
    const { panels, totalVol, totalMass } = buildLoadSummary(load, panelsById);
    panels.forEach((p) =>
      rows.push([load.label, p.jobNumber, p.name, p.width, p.length, p.thickness, p.area, p.volume, (p.mass / 1000).toFixed(2), p.strength28, p.shape])
    );
    if (panels.length) rows.push([load.label, "TOTAL", "", "", "", "", "", totalVol.toFixed(2), (totalMass / 1000).toFixed(2), "", ""]);
  });
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const prefix = projectName ? slug(projectName) + "_" : "";
  a.download = `${prefix}concrete_booking_all_loads.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadDeliveryScheduleCsv(loads, panelsById, projectName = "") {
  const used = loads.filter((l) => (l.panelIds || []).length > 0);
  const header = ["Seq", "Load", "Panels", "Volume(m3)", "Mass(t)", "Strength(MPa)", "Jobs", "Over Capacity"];
  const rows = [];
  if (projectName) rows.push([`Yard: ${projectName}`]);
  rows.push(header);
  let seq = 0;
  let totVol = 0;
  let totMass = 0;
  used.forEach((load) => {
    const { panels, totalVol, totalMass, jobs, mpa } = buildLoadSummary(load, panelsById);
    seq += 1;
    totVol += totalVol;
    totMass += totalMass;
    rows.push([
      seq,
      load.label,
      panels.length,
      totalVol.toFixed(2),
      (totalMass / 1000).toFixed(2),
      mpa,
      jobs.join(" / "),
      totalVol > LOAD_CAPACITY ? "YES" : "",
    ]);
  });
  rows.push([]);
  rows.push(["", "TOTAL", "", totVol.toFixed(2), (totMass / 1000).toFixed(2), "", "", ""]);
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const prefix = projectName ? slug(projectName) + "_" : "";
  a.download = `${prefix}delivery_schedule.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function printLoadSheet(load, panelsById, projectName = "") {
  const { panels, totalVol, totalMass, jobs, mpa, strengths } = buildLoadSummary(load, panelsById);
  const over = totalVol > LOAD_CAPACITY;
  const mixed = strengths.length > 1;
  const date = new Date().toLocaleString();

  const rowsHtml = panels
    .map(
      (p, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${p.jobNumber || "—"}</td>
        <td><b>${p.name}</b></td>
        <td>${fmt(p.width)} × ${fmt(p.length)}</td>
        <td>${fmt(p.thickness, 3)}</td>
        <td>${fmt(p.area)}</td>
        <td>${fmt(p.volume, 3)}</td>
        <td>${fmt(p.mass / 1000, 2)}</td>
        <td>${fmt(p.strength28)}</td>
        <td>${p.shape}</td>
      </tr>`
    )
    .join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"/>
  <title>${load.label} — Concrete Booking Sheet</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 28px; }
    .head { display:flex; align-items:center; gap:16px; border-bottom:3px solid #2560C9; padding-bottom:12px; }
    .head img { height:54px; }
    .head h1 { font-size:20px; margin:0; letter-spacing:1px; }
    .head p { margin:2px 0 0; color:#555; font-size:11px; text-transform:uppercase; letter-spacing:2px; }
    .meta { display:flex; gap:24px; margin:18px 0; flex-wrap:wrap; }
    .card { border:1px solid #ddd; padding:10px 14px; min-width:130px; }
    .card .l { font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#777; }
    .card .v { font-size:18px; font-weight:bold; margin-top:2px; }
    .over { color:#d11; }
    table { width:100%; border-collapse:collapse; margin-top:8px; font-size:12px; }
    th, td { border:1px solid #ccc; padding:6px 8px; text-align:left; }
    th { background:#2560C9; color:#fff; font-size:10px; text-transform:uppercase; letter-spacing:.5px; }
    tfoot td { font-weight:bold; background:#f2f5fb; }
    .foot { margin-top:22px; font-size:10px; color:#888; border-top:1px solid #eee; padding-top:8px; }
    .sign { margin-top:34px; display:flex; gap:60px; }
    .sign div { border-top:1px solid #333; padding-top:6px; font-size:11px; width:220px; }
  </style></head><body>
  <div class="head">
    <img src="${LOGO_URL}" alt="EAZIKAST"/>
    <div><h1>CONCRETE BOOKING SHEET</h1><p>${projectName ? projectName + " · " : ""}EAZIKAST — Yard Organizer</p></div>
  </div>
  <div class="meta">
    <div class="card"><div class="l">Load</div><div class="v">${load.label}</div></div>
    <div class="card"><div class="l">Panels</div><div class="v">${panels.length}</div></div>
    <div class="card"><div class="l">Total Volume</div><div class="v ${over ? "over" : ""}">${fmt(totalVol, 2)} / ${LOAD_CAPACITY} m³</div></div>
    <div class="card"><div class="l">Total Mass</div><div class="v">${fmt(totalMass / 1000, 2)} t</div></div>
    <div class="card"><div class="l">Strength (f'c)</div><div class="v ${mixed ? "over" : ""}" style="font-size:15px">${mpa}</div></div>
    <div class="card"><div class="l">Job Numbers</div><div class="v" style="font-size:14px">${jobs.join(", ") || "—"}</div></div>
    <div class="card"><div class="l">Date</div><div class="v" style="font-size:12px">${date}</div></div>
  </div>
  ${over ? '<p class="over" style="font-weight:bold">⚠ WARNING: This load exceeds the ' + LOAD_CAPACITY + ' m³ capacity.</p>' : ""}
  <table>
    <thead><tr>
      <th>#</th><th>Job</th><th>Mark</th><th>W × L (m)</th><th>Thick (m)</th>
      <th>Area (m²)</th><th>Vol (m³)</th><th>Mass (t)</th><th>f'c (MPa)</th><th>Shape</th>
    </tr></thead>
    <tbody>${rowsHtml || '<tr><td colspan="10" style="text-align:center;color:#999">No panels in this load</td></tr>'}</tbody>
    <tfoot><tr>
      <td colspan="6" style="text-align:right">TOTAL</td>
      <td>${fmt(totalVol, 2)}</td><td>${fmt(totalMass / 1000, 2)}</td><td colspan="2"></td>
    </tr></tfoot>
  </table>
  <div class="sign">
    <div>Prepared by</div><div>Batching plant received by</div>
  </div>
  <div class="foot">Generated by EAZIKAST Yard Organizer · ${date}</div>
  </body></html>`;

  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 500);
}
